import React, { useEffect, useRef, useState } from 'react';

// Shared pieces of the dashboard charts (Sales Dashboard's Sales by Period,
// CRM Dashboard's Activities), so they read as one family: same toggle,
// bar shape and width, baseline, hover highlight and hover box. Plain SVG,
// no chart library. See ui-architecture.md "Dashboard Charts".

// Widest a single bar (or stack) gets, however few bars there are.
export const MAX_BAR_WIDTH = 48;
// Gap between stacked segments, and the rounding on a bar's top corners.
export const STACK_GAP = 2;
export const BAR_RADIUS = 4;
export const BASELINE_COLOR = '#b3bbc7';
// The highlight behind the hovered, or drilled-into, period.
export const HIGHLIGHT_COLOR = '#eef3fb';
// How the chart fades while a new range or filter loads.
export const loadingFade = (loading) => ({ opacity: loading ? 0.55 : 1, transition: 'opacity 0.15s' });

// The width of an element, kept up to date as it resizes, so a chart can
// fill whatever room its column has.
export function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

// A rect with only its top corners rounded, so a bar stays anchored flat
// on the baseline.
export function topRoundedBar(x, y, w, h, r = BAR_RADIUS) {
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h} L${x},${y + rr} Q${x},${y} ${x + rr},${y} L${x + w - rr},${y} Q${x + w},${y} ${x + w},${y + rr} L${x + w},${y + h} Z`;
}

// A row of joined buttons, one active. options: [{ key, label, title }];
// isDisabled(option) greys one out (its title then says why, via
// disabledTitle(option)).
export function SegmentedToggle({ options, value, onChange, isDisabled, disabledTitle }) {
  return (
    <span style={{ display: 'inline-flex' }}>
      {options.map((opt, i) => {
        const active = value === opt.key;
        const disabled = isDisabled ? isDisabled(opt) : false;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => !disabled && onChange(opt.key)}
            disabled={disabled}
            title={disabled && disabledTitle ? disabledTitle(opt) : opt.title}
            style={{
              border: '1px solid #b3bbc7',
              borderLeft: i === 0 ? '1px solid #b3bbc7' : 'none',
              borderRadius: i === 0 ? '4px 0 0 4px' : (i === options.length - 1 ? '0 4px 4px 0' : 0),
              background: active ? '#1976d2' : '#fff',
              color: active ? '#fff' : (disabled ? '#b0b6c0' : '#333'),
              fontSize: 12,
              fontWeight: 600,
              padding: '4px 10px',
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </span>
  );
}

// The box shown beside a hovered period: a heading, one row per series
// ({ key, color, name, value }, name optional), an optional Total row and a
// click hint. Placed in the chart's position: relative wrapper; anchorLeft /
// anchorRight are the hovered period's left and right edges, and the box
// flips to the left side once the period is past 60% of the chart's width.
export function ChartHoverBox({ chartWidth, anchorLeft, anchorRight, top = 0, heading, rows, total, totalDetail, hint }) {
  const flip = (anchorLeft + anchorRight) / 2 > chartWidth * 0.6;
  return (
    <div
      style={{
        position: 'absolute',
        top,
        ...(flip ? { right: chartWidth - anchorLeft + 6 } : { left: anchorRight + 6 }),
        background: '#fff',
        border: '1px solid #d0d5dd',
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(16, 24, 40, 0.12)',
        padding: '8px 10px',
        fontSize: 12,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        zIndex: 2,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#222' }}>{heading}</div>
      {rows.map((r) => (
        <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '1px 0' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flexShrink: 0 }} />
          {r.name && <span style={{ color: '#555', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>}
          <span style={{ marginLeft: 'auto', paddingLeft: 12, fontWeight: 600, color: '#222' }}>{r.value}</span>
          {r.detail && <span style={{ color: '#8a94a6', minWidth: 64, textAlign: 'right' }}>{r.detail}</span>}
        </div>
      ))}
      {total !== undefined && (
        <div style={{ display: 'flex', borderTop: '1px solid #e3e7ee', marginTop: 4, paddingTop: 4 }}>
          <span style={{ color: '#555' }}>Total</span>
          <span style={{ marginLeft: 'auto', paddingLeft: 12, fontWeight: 700, color: '#222' }}>{total}</span>
          {totalDetail && <span style={{ color: '#8a94a6', minWidth: 64, textAlign: 'right', marginLeft: 6 }}>{totalDetail}</span>}
        </div>
      )}
      {hint && <div style={{ marginTop: 6, color: '#8a94a6', fontSize: 11 }}>{hint}</div>}
    </div>
  );
}
